import { useState } from 'react';
import { QuizQuestion } from '../../data/questions';

const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

interface QuestionCardProps {
  question: QuizQuestion;
  selectedOption: number | null;
  onSelect: (optionIndex: number) => void;
}

export function QuestionCard({
  question,
  selectedOption,
  onSelect,
}: QuestionCardProps) {
  const [pendingSelection, setPendingSelection] = useState<number | null>(null);

  function handleSelect(index: number) {
    if (pendingSelection !== null) return;
    setPendingSelection(index);
    // Auto-advance after brief visual confirmation
    setTimeout(() => {
      onSelect(index + 1); // convert 0-based to 1-based (1=Driver, 2=Expressive, etc.)
    }, 280);
  }

  return (
    <div>
      <p className="text-sm font-semibold uppercase tracking-widest text-brand-slate mb-2">
        Choose one word that is most like you
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {question.options.map((option, index) => {
          const letter = OPTION_LETTERS[index];
          const isSelected = pendingSelection === index || selectedOption === index + 1;

          return (
            <button
              key={index}
              onClick={() => handleSelect(index)}
              disabled={pendingSelection !== null}
              className={`
                flex items-center gap-3 px-5 py-4 rounded-lg border-2 text-left
                transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:ring-offset-2
                ${
                  isSelected
                    ? 'border-brand bg-brand text-white shadow-md'
                    : 'border-gray-200 bg-white text-brand hover:border-brand-slate hover:shadow-sm'
                }
                ${pendingSelection !== null && pendingSelection !== index ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <span
                className={`
                  flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${isSelected ? 'bg-white text-brand' : 'bg-porcelain text-brand-slate'}
                `}
              >
                {letter}
              </span>
              <span className="font-medium text-base">{option}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
